import apiClient from './client';
import { AuthResponse, LoginRequest, RegisterRequest, SessionInfo, User } from '../types/auth';
import { isDesktopRuntime } from '../offline/db';

/**
 * Authentication API Service
 */
export const authAPI = {
  /**
   * Login with email and password. The desktop shell has its own endpoint
   * that issues a 30-day refresh token (vs. 1 day for web) so offline use
   * isn't cut short -- and labels the session correctly as "desktop" in
   * the active-sessions list (see listSessions below).
   */
  login: (credentials: LoginRequest) =>
    apiClient.post<AuthResponse>(isDesktopRuntime() ? '/token/desktop/' : '/token/', credentials),

  /**
   * Refresh access token
   */
  refreshToken: (refresh: string) =>
    apiClient.post<AuthResponse>('/token/refresh/', { refresh }),

  /**
   * Get current authenticated user
   */
  getCurrentUser: () =>
    apiClient.get<User>('/users/me/'),

  /**
   * Register new user
   */
  register: (data: RegisterRequest) =>
    apiClient.post<User>('/users/', data),

  /**
   * Update user profile
   */
  updateProfile: (data: Partial<User>) =>
    apiClient.put<User>('/users/me/', data),

  /**
   * Record acceptance of the current Terms and Conditions
   */
  acceptTerms: () =>
    apiClient.post<User>('/users/accept_terms/'),

  /**
   * Record acceptance of the current Privacy Policy
   */
  acceptPrivacy: () =>
    apiClient.post<User>('/users/accept_privacy/'),

  /**
   * Change the authenticated user's password
   */
  changePassword: (data: { current_password: string; new_password: string }) =>
    apiClient.post('/users/change_password/', data),

  /**
   * Request an email change - sends a confirmation link to the new address
   */
  requestEmailChange: (new_email: string) =>
    apiClient.post('/users/request_email_change/', { new_email }),

  /**
   * Confirm an email change with a token
   */
  confirmEmailChange: (token: string) =>
    apiClient.post('/users/confirm-email-change/', { token }),

  /**
   * Logout (client-side only, no API call needed)
   */
  logout: () => {
    // Logout is handled client-side by clearing tokens
    return Promise.resolve();
  },

  /**
   * Verify email with token
   */
  verifyEmail: (token: string) =>
    apiClient.post('/users/verify-email/', { token }),

  /**
   * Resend verification email
   */
  resendVerification: (email: string) =>
    apiClient.post('/users/resend-verification/', { email }),

  /**
   * Request password reset
   */
  requestPasswordReset: (email: string) =>
    apiClient.post('/users/request-password-reset/', { email }),

  /**
   * Reset password with token
   */
  resetPassword: (token: string, password: string) =>
    apiClient.post('/users/reset-password/', { token, password }),

  /**
   * List the authenticated user's active sessions (max 2 -- logging in on
   * a 3rd device auto-signs-out the least-recently-active one).
   */
  listSessions: () =>
    apiClient.get<SessionInfo[]>('/sessions/'),

  /**
   * Manually sign out one active session by id.
   */
  signOutSession: (id: string) =>
    apiClient.delete(`/sessions/${id}/`),
};
